import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartCount extends Component {
  requiredRefs = ["cartBubbleCount"];

  /** @type {number} */
  get currentCartCount() {
    return this.#parseCount(this.refs.cartBubbleCount?.textContent);
  }

  set currentCartCount(value) {
    const count = this.#normalizeCount(value);
    if (!this.refs.cartBubbleCount) return;
    this.refs.cartBubbleCount.textContent = count < 100 ? String(count) : "99+";
  }

  get inDrawer() {
    return this.dataset.context === "drawer";
  }

  /**
   * DAC customization: coerce cart counts so empty/NaN never leave a visible blank badge.
   * @param {unknown} value
   * @returns {number}
   */
  #normalizeCount(value) {
    const count = typeof value === "number" ? value : parseInt(String(value ?? ""), 10);
    if (!Number.isFinite(count) || count <= 0) return 0;
    return Math.floor(count);
  }

  /**
   * @param {string | null | undefined} text
   * @returns {number}
   */
  #parseCount(text) {
    return this.#normalizeCount(text ?? "0");
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event
   * Supports both incremental (CartAddEvent) and absolute (CartUpdateEvent) count updates
   * @param {Event} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = this.#normalizeCount(event.detail.data?.itemCount ?? 0);
    const isIncremental = event.detail.data?.isIncremental ?? false;

    if (isIncremental) {
      // Incremental update: add delta to current count
      let currentCount = this.#parseCount(this.refs.cartBubbleCount?.textContent);

      if (!this.inDrawer) {
        currentCount += itemCount;
      }

      this.renderCartBubble(currentCount);
    } else {
      // Absolute update: set count directly from server
      this.renderCartBubble(itemCount);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   */
  renderCartBubble = async (itemCount) => {
    const count = this.#normalizeCount(itemCount);
    const isEmpty = count === 0;

    this.currentCartCount = count;

    // DAC customization: dedicated empty class for CSS hide (also keep legacy hidden hooks)
    this.classList.toggle("cart-count--empty", isEmpty);
    this.classList.toggle("hidden", isEmpty);
    this.toggleAttribute("hidden", isEmpty);
    this.classList.toggle("cart-count--small-medium", count > 99);
    document.body.classList.toggle("cart-has-items", count > 0);
  };
}

if (!customElements.get("cart-count")) {
  customElements.define("cart-count", CartCount);
}
